import React, { useEffect, useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatCurrency, calculateInstallments } from '@/lib/formatters';
import { InstallmentOptionsDto, invoicingApi } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import { getDeliveryTypeTotalUtil } from '@/lib/utils';
import { TooltipWrapper } from '@/components/shared/TooltipWrapper';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Calendar, CreditCard, Info, CalendarDays } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useUser } from '@/contexts/UserContext';

const CASH_PAYMENT_OPTION = 0;
const CUSTOM_PAYMENT_PLAN_ID = 3;

interface InstallmentChoice {
  key: string;
  installmentQuantity: number;
  condition: string | null;
  months: number;
}

interface OrderItem {
  id: number;
  codigo_produto: string;
  descricao_produto: string;
  preco: number;
  ano_letivo: number;
  marca: string;
  sku: string;
  status: string;
  utilizacao?: string | null;
  serie?: string | null;
  segmento?: string | null;
  quantidade_contratual: number;
  desconto: number;
  quantidade_pedido: number;
  valor_liquido: number;
  valor_total: number;
  delivery_type_id: number;
  delivery_type_number: number;
  brand_id: number;
}

interface InstallmentPlanSectionProps {
  orderItems: OrderItem[];
  freteAnual: number;
  freteSemestral: number;
  /** Desconto do cupom abatido do total anual/semestral antes de calcular as parcelas. */
  couponDiscountAnnual?: number;
  couponDiscountSemestral?: number;
  isScheduled: boolean;
  onAnnualInstallmentsChange?: (value: number) => void;
  onSemestralInstallmentsChange?: (value: number) => void;
  onAnnualConditionChange?: (value: string | null) => void;
  onSemestralConditionChange?: (value: string | null) => void;
  onLoadingStateChange?: (isLoading: boolean, hasData: boolean) => void;
  cycleYear?: number | null;
}

const InstallmentPlanSection = ({
  orderItems,
  isScheduled,
  freteAnual = 0,
  freteSemestral = 0,
  couponDiscountAnnual = 0,
  couponDiscountSemestral = 0,
  onAnnualInstallmentsChange,
  onSemestralInstallmentsChange,
  onAnnualConditionChange,
  onSemestralConditionChange,
  onLoadingStateChange,
  cycleYear,
}: InstallmentPlanSectionProps) => {
  const { selectedClient } = useUser();
  const cnpj = selectedClient?.cnpj ? String(selectedClient.cnpj) : '';

  const [annualData, setAnnualData] = useState<InstallmentOptionsDto | null>(null);
  const [semestralData, setSemestralData] = useState<InstallmentOptionsDto | null>(null);
  const [selectedAnnualQtd, setSelectedAnnualQtd] = useState<number>(0);
  const [selectedSemestralQtd, setSelectedSemestralQtd] = useState<number>(0);
  const [selectedAnnualChoiceKey, setSelectedAnnualChoiceKey] = useState<string>('');
  const [selectedSemestralChoiceKey, setSelectedSemestralChoiceKey] = useState<string>('');

  const {
    data: installmentOptions,
    isLoading,
  } = useQuery({
    queryKey: ["installmentOptions", cnpj, cycleYear ?? null],
    queryFn: async () => {
      const targetDate = new Date();
      const response = await invoicingApi.installmentOptions(targetDate.getMonth() + 1, targetDate.getFullYear(), cnpj, cycleYear);
      return response?.data ?? [];
    },
    enabled: !!cnpj,
    refetchOnWindowFocus: true,
  });

  const getInstallmentChoices = (data: InstallmentOptionsDto): InstallmentChoice[] => {
    const isCustomPlan = data.paymentPlan?.paymentPlanId === CUSTOM_PAYMENT_PLAN_ID;

    if (isCustomPlan && data.optionsConfiguration?.length) {
      return data.optionsConfiguration
        .map((configuration, index) => ({
          key: configuration.paymentConfigurationId || `${configuration.condition}-${index}`,
          installmentQuantity: configuration.installmentQuantity ?? configuration.option,
          condition: configuration.condition || null,
          months: configuration.months ? parseInt(configuration.months, 10) : 1,
        }))
        .sort((first, second) => first.installmentQuantity - second.installmentQuantity
          || (first.condition ?? '').localeCompare(second.condition ?? ''));
    }

    const seen = new Set<number>();
    const quantities = data.fullPaymentOnly
      ? [CASH_PAYMENT_OPTION]
      : (data.availableOptions?.length
        ? data.availableOptions
        : (data.optionsConfiguration?.map(configuration => configuration.installmentQuantity ?? configuration.option) ?? []));

    return quantities
      .filter(quantity => !seen.has(quantity) && seen.add(quantity))
      .sort((first, second) => first - second)
      .map(quantity => {
        const configuration = data.optionsConfiguration?.find(item =>
          (item.installmentQuantity ?? item.option) === quantity);
        return {
          key: `installments-${quantity}`,
          installmentQuantity: quantity,
          condition: configuration?.condition || null,
          months: configuration?.months ? parseInt(configuration.months, 10) : 1,
        };
      });
  };

  const getChoice = (data: InstallmentOptionsDto | null, key: string) =>
    data ? getInstallmentChoices(data).find(choice => choice.key === key) : undefined;

  useEffect(() => {
    if (installmentOptions) {
      const annual = installmentOptions.find(item => item.deliveryTypeId === 1);
      const semestral = installmentOptions.find(item => item.deliveryTypeId === 2);

      if (annual) {
        setAnnualData(annual);
        const choices = getInstallmentChoices(annual);
        const defaultChoice = choices[choices.length - 1];
        const defaultQuantity = defaultChoice?.installmentQuantity ?? 0;
        setSelectedAnnualChoiceKey(defaultChoice?.key ?? '');
        setSelectedAnnualQtd(defaultQuantity);
        onAnnualInstallmentsChange?.(defaultQuantity);
        onAnnualConditionChange?.(defaultChoice?.condition ?? null);
      }
      if (semestral) {
        setSemestralData(semestral);
        const choices = getInstallmentChoices(semestral);
        const defaultChoice = choices[choices.length - 1];
        const defaultQuantity = defaultChoice?.installmentQuantity ?? 0;
        setSelectedSemestralChoiceKey(defaultChoice?.key ?? '');
        setSelectedSemestralQtd(defaultQuantity);
        onSemestralInstallmentsChange?.(defaultQuantity);
        onSemestralConditionChange?.(defaultChoice?.condition ?? null);
      }
    }
  }, [installmentOptions]);

  const totalAnnual = useMemo(() => {
    const base = getDeliveryTypeTotalUtil(orderItems, 1, isScheduled);
    return isScheduled ? base : Math.max(0, base + freteAnual - couponDiscountAnnual);
  }, [orderItems, isScheduled, freteAnual, couponDiscountAnnual]);

  const totalSemestral = useMemo(() => {
    const base = getDeliveryTypeTotalUtil(orderItems, 2, isScheduled);
    return isScheduled ? base : Math.max(0, base + freteSemestral - couponDiscountSemestral);
  }, [orderItems, isScheduled, freteSemestral, couponDiscountSemestral]);

  useEffect(() => {
    const hasItems = totalAnnual > 0 || totalSemestral > 0;
    const hasData = Array.isArray(installmentOptions) && installmentOptions.length > 0;
    const isDataValid = !hasItems || hasData;
    onLoadingStateChange?.(isLoading, isDataValid);
  }, [isLoading, installmentOptions, totalAnnual, totalSemestral, onLoadingStateChange]);

  // Calcular parcelas com memoização
  const annualInstallments = useMemo(() =>
    calculateInstallments(totalAnnual, selectedAnnualQtd === CASH_PAYMENT_OPTION ? 1 : selectedAnnualQtd),
    [totalAnnual, selectedAnnualQtd]);

  const semestralInstallments = useMemo(() =>
    calculateInstallments(totalSemestral, selectedSemestralQtd === CASH_PAYMENT_OPTION ? 1 : selectedSemestralQtd),
    [totalSemestral, selectedSemestralQtd]);

  // Meses em português
  const months = useMemo(() => [
    'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
    'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
  ], []);

  const getPeriodText = (data: InstallmentOptionsDto, monthsOffset: number, quantity: number) => {
    if (quantity <= 0) return "";

    // Calcula a cobrança a partir do mês/ano da consulta, incluindo viradas de ano.
    const startDate = new Date(data.startYear, data.startMonth - 1 + monthsOffset, 1);

    // Usa setMonth que lida perfeitamente com viradas de ano no JS
    const endDate = new Date(startDate);
    endDate.setMonth(startDate.getMonth() + quantity - 1);

    // Se for 1 parcela, mostra apenas o mês, não o "período" A a B
    if (quantity === 1) {
      return `${months[startDate.getMonth()]}/${startDate.getFullYear()}`;
    }

    return `${months[startDate.getMonth()]}/${startDate.getFullYear()} a ${months[endDate.getMonth()]}/${endDate.getFullYear()}`;
  };

  // Calcula o mês do 5º dia corrido a partir de hoje (À vista)
  const getAVistaMes = () => {
    const date = new Date();
    date.setDate(date.getDate() + 5);
    return `${months[date.getMonth()]}/${date.getFullYear()}`;
  };

  const annualPeriodText = useMemo(() => {
    if (selectedAnnualQtd === CASH_PAYMENT_OPTION) return getAVistaMes();
    const monthsOffset = getChoice(annualData, selectedAnnualChoiceKey)?.months ?? 1;
    return annualData ? getPeriodText(annualData, monthsOffset, selectedAnnualQtd) : "";
  }, [annualData, selectedAnnualQtd, selectedAnnualChoiceKey, months]);

  const semestralPeriodText = useMemo(() => {
    if (selectedSemestralQtd === CASH_PAYMENT_OPTION) return getAVistaMes();
    const monthsOffset = getChoice(semestralData, selectedSemestralChoiceKey)?.months ?? 1;
    return semestralData ? getPeriodText(semestralData, monthsOffset, selectedSemestralQtd) : "";
  }, [semestralData, selectedSemestralQtd, selectedSemestralChoiceKey, months]);

  if (isLoading) {
    return (
      <Card className="shadow-lg border-primary/20 animate-pulse">
        <CardHeader><div className="h-6 w-48 bg-muted rounded"></div></CardHeader>
        <CardContent className="space-y-4">
          <div className="h-24 bg-muted rounded"></div>
          <div className="h-24 bg-muted rounded"></div>
        </CardContent>
      </Card>
    );
  }

  // Não renderizar se não há produtos anuais nem semestrais
  if (totalAnnual <= 0 && totalSemestral <= 0) {
    return null;
  }

  return (
    <TooltipProvider>
      <Card className="shadow-lg border-primary/20 overflow-hidden">
        <CardHeader className="pb-4 bg-primary/[0.02] border-b border-primary/10">
          <CardTitle className="text-xl font-bold text-primary flex items-center gap-2">
            <TooltipWrapper content="As condições de parcelamento são calculadas com base na data de hoje.">
              <CreditCard className="h-5 w-5" />
            </TooltipWrapper>
            Plano de Parcelas
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-6">
          {/* Pedido Anual */}
          {totalAnnual > 0 && annualData && (
            <div className="p-4 bg-background rounded-lg border border-l-4 border-l-blue-500 shadow-sm transition-all hover:shadow-md hover:border-blue-500/50 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <TooltipWrapper content="Itens com entrega Anual (Ex: Material Principal)">
                    <Calendar className="h-5 w-5 text-blue-500" />
                  </TooltipWrapper>
                  <h3 className="font-semibold text-foreground">Pedido Anual</h3>
                </div>
                <span className="font-bold text-lg text-primary tabular-nums">
                  {formatCurrency(totalAnnual)}
                </span>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
                  {annualData.fullPaymentOnly ? 'Condição de Pagamento' : 'Quantidade de Parcelas'}
                </label>
                {annualData.fullPaymentOnly ? (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-muted-foreground/20 bg-background text-sm font-medium text-foreground">
                    <CreditCard className="h-4 w-4 text-blue-500" />
                    À vista — {formatCurrency(totalAnnual)}
                  </div>
                ) : (
                  <Select
                    value={selectedAnnualChoiceKey}
                    onValueChange={(val) => {
                      const choice = getChoice(annualData, val);
                      if (!choice) return;
                      setSelectedAnnualChoiceKey(choice.key);
                      setSelectedAnnualQtd(choice.installmentQuantity);
                      onAnnualInstallmentsChange?.(choice.installmentQuantity);
                      onAnnualConditionChange?.(choice.condition);
                    }}
                  >
                    <SelectTrigger className="w-full bg-background border-muted-foreground/20 hover:border-blue-500/50 transition-colors">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {getInstallmentChoices(annualData).map((choice) => (
                        <SelectItem key={choice.key} value={choice.key} className="tabular-nums">
                          {choice.installmentQuantity === CASH_PAYMENT_OPTION && `À vista ${formatCurrency(totalAnnual)}`}
                          {choice.installmentQuantity !== CASH_PAYMENT_OPTION && `${choice.installmentQuantity}x de ${formatCurrency(totalAnnual / choice.installmentQuantity)}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div className="flex justify-between items-end pt-1">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-foreground tabular-nums">
                      {selectedAnnualQtd === CASH_PAYMENT_OPTION ? 'À vista' : `${selectedAnnualQtd} parcelas`} de {formatCurrency(annualInstallments.installmentValue)}
                    </p>
                    {!annualInstallments.allInstallmentsEqual && (
                      <TooltipWrapper content={`A última parcela será de ${formatCurrency(annualInstallments.lastInstallmentValue)} para ajuste de centavos.`}>
                        <Info className="h-3.5 w-3.5 text-amber-500 cursor-help" />
                      </TooltipWrapper>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <span className="opacity-70">Período:</span>
                    <span className="font-medium">{annualPeriodText}</span>
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Pedido Semestre */}
          {totalSemestral > 0 && semestralData && (
            <div className="p-4 bg-background rounded-lg border border-l-4 border-l-indigo-500 shadow-sm transition-all hover:shadow-md hover:border-indigo-500/50 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <TooltipWrapper content="Itens com entrega Semestral (Ex: Livros Consumíveis)">
                    <CalendarDays className="h-5 w-5 text-indigo-500" />
                  </TooltipWrapper>
                  <h3 className="font-semibold text-foreground">Pedido Semestral</h3>
                </div>
                <span className="font-bold text-lg text-primary tabular-nums">
                  {formatCurrency(totalSemestral)}
                </span>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
                  {semestralData.fullPaymentOnly ? 'Condição de Pagamento' : 'Quantidade de Parcelas'}
                </label>
                {semestralData.fullPaymentOnly ? (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-muted-foreground/20 bg-background text-sm font-medium text-foreground">
                    <CreditCard className="h-4 w-4 text-indigo-500" />
                    À vista — {formatCurrency(totalSemestral)}
                  </div>
                ) : (
                  <Select
                    value={selectedSemestralChoiceKey}
                    onValueChange={(val) => {
                      const choice = getChoice(semestralData, val);
                      if (!choice) return;
                      setSelectedSemestralChoiceKey(choice.key);
                      setSelectedSemestralQtd(choice.installmentQuantity);
                      onSemestralInstallmentsChange?.(choice.installmentQuantity);
                      onSemestralConditionChange?.(choice.condition);
                    }}
                  >
                    <SelectTrigger className="w-full bg-background border-muted-foreground/20 hover:border-indigo-500/50 transition-colors">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {getInstallmentChoices(semestralData).map((choice) => (
                        <SelectItem key={choice.key} value={choice.key} className="tabular-nums">
                          {choice.installmentQuantity === CASH_PAYMENT_OPTION && `À vista ${formatCurrency(totalSemestral)}`}
                          {choice.installmentQuantity !== CASH_PAYMENT_OPTION && `${choice.installmentQuantity}x de ${formatCurrency(totalSemestral / choice.installmentQuantity)}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div className="flex justify-between items-end pt-1">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-foreground tabular-nums">
                      {selectedSemestralQtd === CASH_PAYMENT_OPTION ? 'À vista' : `${selectedSemestralQtd} parcelas`} de {formatCurrency(semestralInstallments.installmentValue)}
                    </p>
                    {!semestralInstallments.allInstallmentsEqual && (
                      <TooltipWrapper content={`A última parcela será de ${formatCurrency(semestralInstallments.lastInstallmentValue)} para ajuste de centavos.`}>
                        <Info className="h-3.5 w-3.5 text-amber-500 cursor-help" />
                      </TooltipWrapper>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <span className="opacity-70">Período:</span>
                    <span className="font-medium">{semestralPeriodText}</span>
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Informação sobre condições de entrega */}
          <div className="text-xs text-muted-foreground bg-blue-50/50 dark:bg-blue-950/20 p-4 rounded-lg border border-blue-200/50 dark:border-blue-800/50">
            <div className="flex gap-3">
              <Info className="text-blue-600 dark:text-blue-400 h-4 w-4 flex-shrink-0 mt-0.5" />
              <div className="space-y-3">
                <p className="leading-relaxed">
                  Informamos que a entrega dos pedidos está condicionada à ausência de pendências financeiras, aprovação em análise de crédito e ao cumprimento das condições contratuais vigentes.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                  <a href="https://ajuda.conexia.com.br/hc/pt-br/requests/new" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-2 bg-white/50 dark:bg-black/20 rounded border border-blue-100 dark:border-blue-900 hover:bg-blue-100 transition-colors group">
                    <span className="text-blue-600">🎧</span>
                    <span className="group-hover:text-blue-700">Central de Ajuda</span>
                  </a>
                  <a href="https://api.whatsapp.com/send/?phone=5508003458000&text&type=phone_number&app_absent=0" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-2 bg-white/50 dark:bg-black/20 rounded border border-blue-100 dark:border-blue-900 hover:bg-blue-100 transition-colors group">
                    <span className="text-green-600">💬</span>
                    <span className="group-hover:text-blue-700">WhatsApp: 0800-345-8000</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </TooltipProvider>
  );
};

export default InstallmentPlanSection;
